import React, { useState, useEffect, useMemo } from "react"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import Select from "../../../components/ui/Select"
import Button from "../../../components/ui/Button"
import { Card, CardContent } from "../../../components/ui/card"
import { Badge } from "../../../components/ui/badge"
import { listCourses } from "../../../utils/courseHelpers"
import { listPrograms } from "../../../utils/programHelpers"
import { listCohorts } from "../../../utils/cohortHelpers"
import {
  createInvitation,
  listOrgInvitations,
  revokeInvitation,
  getInvitationDisplayStatus,
  computeInvitationMetrics,
} from "../../../utils/invitationHelpers"

const MetricTile = ({ label, value }) => (
  <div className="p-4 rounded-2xl bg-foreground/[0.02] border border-foreground/[0.06]">
    <p className="text-sm text-muted-foreground mb-1">{label}</p>
    <p className="text-2xl font-semibold text-foreground">{value}</p>
  </div>
)

const STATUS_BADGE = {
  accepted: { variant: "success", label: "Accepted" },
  opened: { variant: "default", label: "Opened" },
  pending: { variant: "secondary", label: "Pending" },
  expired: { variant: "warning", label: "Expired" },
}

// Single-learner + cohort invites (named, email required) and shareable
// org/program links (unnamed, email optional — anyone with the link can
// use it) both go through the same createInvitation() call, just with
// email left blank for the link case. Bulk CSV loops the same call per row.
const InvitationsPanel = ({ organization }) => {
  const orgId = organization?.orgId
  const orgName = organization?.name
  const invitedByName = organization?.ownerName || organization?.name

  const [courses, setCourses] = useState([])
  const [programs, setPrograms] = useState([])
  const [cohorts, setCohorts] = useState([])
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)

  const [mode, setMode] = useState(null) // "learner" | "cohort" | "bulk" | "link"
  const [courseId, setCourseId] = useState("")
  const [cohortId, setCohortId] = useState("")
  const [programId, setProgramId] = useState("")
  const [email, setEmail] = useState("")
  const [bulkText, setBulkText] = useState("")
  const [linkType, setLinkType] = useState("course") // "course" | "program"
  const [generatedLink, setGeneratedLink] = useState("")
  const [copied, setCopied] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState("")
  const [formMessage, setFormMessage] = useState("")

  const loadAll = async () => {
    setLoading(true)
    const [{ courses: courseList }, { programs: programList }, { cohorts: cohortList }, { invitations: inviteList }] = await Promise.all([
      listCourses(orgId),
      listPrograms(orgId),
      listCohorts(orgId),
      listOrgInvitations(orgId),
    ])
    setCourses(courseList || [])
    setPrograms(programList || [])
    setCohorts(cohortList || [])
    setInvitations(inviteList || [])
    setLoading(false)
  }

  useEffect(() => {
    if (orgId) loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  const metrics = useMemo(() => computeInvitationMetrics(invitations), [invitations])

  const cohortsForCourse = cohorts?.filter((c) => c?.courseId === courseId)

  const resetForm = () => {
    setCourseId("")
    setCohortId("")
    setProgramId("")
    setEmail("")
    setBulkText("")
    setGeneratedLink("")
    setCopied(false)
    setFormError("")
    setFormMessage("")
  }

  const openMode = (next) => {
    resetForm()
    setMode(next)
  }

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      // clipboard can fail on non-HTTPS/older browsers — link is still visible on screen
    }
  }

      const handleInviteLearner = async () => {
    if (!email?.trim() || !courseId) {
      setFormError("Email and course are both required")
      return
    }
    const course = courses?.find((c) => c?.courseId === courseId)
    const cohort = cohorts?.find((c) => c?.cohortId === cohortId)
    setSaving(true)
    setFormError("")
    const { inviteLink, error } = await createInvitation({
      orgId,
      orgName,
      invitedByName,
      type: cohortId ? "cohort" : "course",
      email,
      courseId,
      courseTitle: course?.title,
      cohortId: cohortId || "",
      cohortName: cohort?.name || "",
    })
    setSaving(false)
    if (error) {
      setFormError(error?.message || "Could not send invitation")
      return
    }
    // Always surface the link, not just when email is unconfigured — the
    // admin may want to share it directly (Slack, WhatsApp) even when email
    // sending works, without relying on the invitee checking their inbox.
    setGeneratedLink(inviteLink)
    setFormMessage("Invitation created.")
    setEmail("")
    loadAll()
  }

  const handleBulkImport = async () => {
    const emails = bulkText
      ?.split(/[\n,]/)
      ?.map((e) => e?.trim())
      ?.filter((e) => e && e.includes("@"))

    if (!courseId) {
      setFormError("Select a course first")
      return
    }
    if (!emails?.length) {
      setFormError("Paste at least one valid email, one per line or comma-separated")
      return
    }

    const course = courses?.find((c) => c?.courseId === courseId)
    const cohort = cohorts?.find((c) => c?.cohortId === cohortId)
    setSaving(true)
    setFormError("")

    // Sequential rather than Promise.all — keeps EmailJS calls from firing
    // in a burst that could hit its rate limit on a bulk import.
    let sentCount = 0
    for (const e of emails) {
      const { error } = await createInvitation({
        orgId,
        orgName,
        invitedByName,
        type: cohortId ? "cohort" : "course",
        email: e,
        courseId,
        courseTitle: course?.title,
        cohortId: cohortId || "",
        cohortName: cohort?.name || "",
      })
      if (!error) sentCount += 1
    }

    setSaving(false)
    setFormMessage(`Sent ${sentCount} of ${emails.length} invitations.`)
    setBulkText("")
    loadAll()
  }

  const handleGenerateLink = async () => {
    if (linkType === "course" && !courseId) {
      setFormError("Select a course first")
      return
    }
    if (linkType === "program" && !programId) {
      setFormError("Select a program first")
      return
    }
    const course = courses?.find((c) => c?.courseId === courseId)
    const program = programs?.find((p) => p?.programId === programId)
    setSaving(true)
    setFormError("")
    const { inviteLink, error } = await createInvitation({
      orgId,
      orgName,
      invitedByName,
      type: linkType,
      email: "",
      courseId: linkType === "course" ? courseId : "",
      courseTitle: linkType === "course" ? course?.title : "",
      programId: linkType === "program" ? programId : "",
      programTitle: linkType === "program" ? program?.title : "",
    })
    setSaving(false)
    if (error) {
      setFormError(error?.message || "Could not generate link")
      return
    }
    setGeneratedLink(inviteLink)
    loadAll()
  }

  const handleRevoke = async (invitationId) => {
    if (!window.confirm("Revoke this invitation? The link will stop working.")) return
    await revokeInvitation(invitationId)
    loadAll()
  }

  const courseOptions = [{ value: "", label: "Select a course..." }, ...(courses?.map((c) => ({ value: c?.courseId, label: c?.title })) || [])]
  const cohortOptions = [{ value: "", label: "No cohort" }, ...(cohortsForCourse?.map((c) => ({ value: c?.cohortId, label: c?.name })) || [])]
  const programOptions = [{ value: "", label: "Select a program..." }, ...(programs?.map((p) => ({ value: p?.programId, label: p?.title })) || [])]

  return (
    <div className="space-y-6 md:col-span-4">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <MetricTile label="Sent" value={metrics.sent} />
        <MetricTile label="Opened" value={metrics.opened} />
        <MetricTile label="Accepted" value={metrics.accepted} />
        <MetricTile label="Pending" value={metrics.pending} />
        <MetricTile label="Expired" value={metrics.expired} />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant={mode === "learner" ? "default" : "outline"} iconName="UserPlus" iconPosition="left" onClick={() => openMode("learner")}>
          Invite Learner
        </Button>
        <Button variant={mode === "cohort" ? "default" : "outline"} iconName="UsersRound" iconPosition="left" onClick={() => openMode("cohort")}>
          Invite to Cohort
        </Button>
        <Button variant={mode === "bulk" ? "default" : "outline"} iconName="Upload" iconPosition="left" onClick={() => openMode("bulk")}>
          Bulk Import
        </Button>
        <Button variant={mode === "link" ? "default" : "outline"} iconName="Link" iconPosition="left" onClick={() => openMode("link")}>
          Enrollment Link
        </Button>
      </div>

      {(mode === "learner" || mode === "cohort") && (
        <Card className="dark:bg-transparent">
          <CardContent className="pt-5 space-y-4">
            <h3 className="text-sm font-semibold text-foreground">
              {mode === "cohort" ? "Invite a learner directly into a cohort" : "Invite a learner"}
            </h3>
            <Input label="Learner email" type="email" placeholder="learner@example.com" value={email} onChange={(e) => setEmail(e?.target?.value)} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Course</label>
                <Select options={courseOptions} value={courseId} onChange={(v) => { setCourseId(v); setCohortId("") }} />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Cohort {mode === "learner" ? "(optional)" : ""}</label>
                <Select options={cohortOptions} value={cohortId} onChange={setCohortId} disabled={!courseId} />
              </div>
            </div>
                        {formError && <p className="text-sm text-destructive">{formError}</p>}
            {formMessage && <p className="text-sm text-success">{formMessage}</p>}
            {generatedLink && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-accent/10 border border-accent/20">
                <code className="text-xs text-foreground flex-1 truncate">{generatedLink}</code>
                <Button variant="outline" size="sm" onClick={() => handleCopy(generatedLink)}>{copied ? "Copied!" : "Copy"}</Button>
              </div>
            )}
            <div className="flex gap-3">
              <Button variant="outline" fullWidth onClick={() => setMode(null)}>Close</Button>
              <Button variant="default" fullWidth loading={saving} onClick={handleInviteLearner}>Send Invitation</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {mode === "bulk" && (
        <Card className="dark:bg-transparent">
          <CardContent className="pt-5 space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Bulk import via CSV / email list</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Course</label>
                <Select options={courseOptions} value={courseId} onChange={(v) => { setCourseId(v); setCohortId("") }} />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Cohort (optional)</label>
                <Select options={cohortOptions} value={cohortId} onChange={setCohortId} disabled={!courseId} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Emails</label>
              <textarea
                rows={6}
                placeholder={"one@example.com\ntwo@example.com\nthree@example.com"}
                value={bulkText}
                onChange={(e) => setBulkText(e?.target?.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">Paste emails one per line, or comma-separated — from a CSV export or straight from a spreadsheet column.</p>
            </div>
                        {formError && <p className="text-sm text-destructive">{formError}</p>}
            {formMessage && <p className="text-sm text-success">{formMessage}</p>}
            {generatedLink && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-accent/10 border border-accent/20">
                <code className="text-xs text-foreground flex-1 truncate">{generatedLink}</code>
                <Button variant="outline" size="sm" onClick={() => handleCopy(generatedLink)}>{copied ? "Copied!" : "Copy"}</Button>
              </div>
            )}
            <div className="flex gap-3">
              <Button variant="outline" fullWidth onClick={() => setMode(null)}>Close</Button>
              <Button variant="default" fullWidth loading={saving} onClick={handleInviteLearner}>Send Invitation</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {mode === "bulk" && (
        <Card className="dark:bg-transparent">
          <CardContent className="pt-5 space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Generate a shareable enrollment link</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Link type</label>
                <Select
                  options={[{ value: "course", label: "Course enrollment" }, { value: "program", label: "Program enrollment" }]}
                  value={linkType}
                  onChange={(v) => { setLinkType(v); setGeneratedLink("") }}
                />
              </div>
              {linkType === "course" ? (
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Course</label>
                  <Select options={courseOptions} value={courseId} onChange={setCourseId} />
                </div>
              ) : (
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Program</label>
                  <Select options={programOptions} value={programId} onChange={setProgramId} />
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Anyone with this link can sign up and be enrolled automatically — no specific email required.</p>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
            {generatedLink && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-accent/10 border border-accent/20">
                <code className="text-xs text-foreground flex-1 truncate">{generatedLink}</code>
                <Button variant="outline" size="sm" onClick={() => handleCopy(generatedLink)}>{copied ? "Copied!" : "Copy"}</Button>
              </div>
            )}
            <div className="flex gap-3">
              <Button variant="outline" fullWidth onClick={() => setMode(null)}>Close</Button>
              <Button variant="default" fullWidth loading={saving} onClick={handleGenerateLink}>Generate Link</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3">Invitation History</h3>
        {loading ? (
          <div className="p-12 text-center text-muted-foreground text-sm">Loading invitations...</div>
        ) : invitations?.length === 0 ? (
          <Card className="p-12 text-center dark:bg-transparent">
            <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Mail" size={24} className="text-accent" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-1">No invitations sent yet</h3>
            <p className="text-muted-foreground text-sm">Invite learners individually, in bulk, or generate a shareable enrollment link.</p>
          </Card>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs text-muted-foreground uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Recipient</th>
                  <th className="text-left px-4 py-3 font-medium">For</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-left px-4 py-3 font-medium">Expires</th>
                  <th className="text-right px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {invitations?.map((inv) => {
                  const status = getInvitationDisplayStatus(inv)
                  const badge = STATUS_BADGE[status]
                  return (
                    <tr key={inv.invitationId} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        {inv.email ? (
                          <p className="text-foreground">{inv.email}</p>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">Shareable link</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {inv.programTitle || inv.courseTitle || "-"}
                        {inv.cohortName && <span className="text-xs"> · {inv.cohortName}</span>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{inv.expiresAt?.slice(0, 10)}</td>
                      <td className="px-4 py-3 text-right">
                        {status !== "accepted" && (
                          <Button variant="ghost" size="icon" iconName="Trash2" iconSize={14} onClick={() => handleRevoke(inv.invitationId)} />
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default InvitationsPanel